import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'

export default function Onboarding({ onComplete }) {
  const { profile } = useAuth()
  const [step, setStep] = useState('welcome')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Template step
  const [templateName, setTemplateName] = useState('')
  const [createdTemplate, setCreatedTemplate] = useState(null)

  // Exercises step
  const [exercises, setExercises] = useState([{ title: '', description: '', sets: '3', reps: '10', file: null, videoUrl: '', saveToLibrary: true }])
  const [savedExercises, setSavedExercises] = useState(false)

  // Invite step
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSent, setInviteSent] = useState(false)

  const STEPS = ['welcome', 'template', 'exercises', 'invite', 'done']

  function progress() {
    const idx = STEPS.indexOf(step)
    return Math.round((idx / (STEPS.length - 1)) * 100)
  }

  function addExercise() {
    setExercises(prev => [...prev, { title: '', description: '', sets: '3', reps: '10' }])
  }

  function removeExercise(i) {
    setExercises(prev => prev.filter((_, idx) => idx !== i))
  }

  function updateExercise(i, field, value) {
    setExercises(prev => prev.map((ex, idx) => idx === i ? { ...ex, [field]: value } : ex))
  }

  async function handleSaveTemplate() {
    if (!templateName.trim()) { setError('Please enter a template name'); return }
    setSaving(true)
    setError('')
    const { data, error: err } = await supabase.from('diagnosis_templates').insert({
      clinic_id: profile.clinic_id,
      name: templateName,
      is_default: false
    }).select().single()
    if (err) { setError(err.message); setSaving(false); return }
    setCreatedTemplate(data)
    setSaving(false)
    setStep('exercises')
  }

  async function handleSaveExercises() {
    const valid = exercises.filter(ex => ex.title.trim())
    if (valid.length === 0) { setError('Add at least one exercise'); return }
    setSaving(true)
    setError('')

    for (const ex of valid) {
      let media_url = null
      if (ex.file) {
        const ext = ex.file.name.split('.').pop()
        const filename = Date.now() + '.' + ext
        const { error: uploadError } = await supabase.storage.from('exercises').upload(filename, ex.file)
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('exercises').getPublicUrl(filename)
          media_url = urlData.publicUrl
        }
      }
      const { data: newEx, error: exErr } = await supabase.from('exercises').insert({
        clinic_id: profile.clinic_id,
        title: ex.title,
        description: ex.description,
        media_url,
        video_url: ex.videoUrl || null
      }).select().single()

      if (exErr) continue

      await supabase.from('diagnosis_template_exercises').insert({
        template_id: createdTemplate.id,
        exercise_id: newEx.id,
        sets: parseInt(ex.sets) || 3,
        reps: parseInt(ex.reps) || 10,
        frequency: 'daily',
        sort_order: valid.indexOf(ex)
      })
    }

    setSaving(false)
    setStep('invite')
  }

  async function handleInvite() {
    if (!inviteName.trim() || !inviteEmail.trim()) { setError('Please enter a name and email'); return }
    setSaving(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/invite-patient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: inviteName, email: inviteEmail, clinic_id: profile.clinic_id })
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Something went wrong'); setSaving(false); return }
      setInviteSent(true)
    } catch {
      setError('Failed to send invite. Please try again.')
    }
    setSaving(false)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>

      {step !== 'welcome' && step !== 'done' && (
        <div style={{ height: 3, background: 'var(--border)' }}>
          <div style={{ height: 3, background: 'var(--brand)', width: progress() + '%', transition: 'width 0.4s ease' }} />
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem' }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          {step !== 'done' && (
            <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
              <button type="button" className="secondary" onClick={onComplete} style={{ fontSize: 13, padding: '6px 14px' }}>
                Skip for now
              </button>
            </div>
          )}

          {error && <div className="alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

          {/* Welcome */}
          {step === 'welcome' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: '1.5rem' }}>👋</div>
              <h1 style={{ marginBottom: 12 }}>Welcome to Complete PT</h1>
              <p style={{ marginBottom: '2rem', fontSize: 16, lineHeight: 1.7 }}>
                Let's get your clinic set up in a few quick steps. It takes about 3 minutes.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left', background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '1.25rem', marginBottom: '2rem' }}>
                {[
                  { icon: '📋', text: 'Create a program template' },
                  { icon: '🏋️', text: 'Add exercises to your template' },
                  { icon: '📧', text: 'Invite your first patient' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{item.icon}</span>
                    <p style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => { setError(''); setStep('template') }} style={{ width: '100%', padding: 16, fontSize: 16 }}>
                Get started →
              </button>
            </div>
          )}

          {/* Template */}
          {step === 'template' && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px' }}>Step 1 of 3</p>
                <h1 style={{ marginBottom: 8 }}>Name your first program</h1>
                <p>Create reusable templates that can be applied to any patient.</p>
              </div>
              <div className="card">
                <div className="form-group">
                  <div className="form-field">
                    <label className="form-label">Program name</label>
                    <input placeholder="e.g. Post-op Knee, Frozen Shoulder, Back Pain" value={templateName} onChange={e => setTemplateName(e.target.value)} autoFocus />
                  </div>
                  <button onClick={handleSaveTemplate} disabled={saving || !templateName.trim()} style={{ padding: 14, fontSize: 15 }}>
                    {saving ? 'Saving...' : 'Save and continue →'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Exercises */}
          {step === 'exercises' && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px' }}>Step 2 of 3</p>
                <h1 style={{ marginBottom: 8 }}>Add exercises to {templateName}</h1>
                <p>These will be saved to your exercise library and added to the program template.</p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
                {exercises.map((ex, i) => (
                  <div key={i} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', margin: 0 }}>Exercise {i + 1}</p>
                      {exercises.length > 1 && (
                        <button type="button" className="danger" onClick={() => removeExercise(i)} style={{ fontSize: 12 }}>Remove</button>
                      )}
                    </div>
                    <div className="form-group">
                      <div className="form-field">
                        <label className="form-label">Exercise name</label>
                        <input placeholder="e.g. Seated Kicks" value={ex.title} onChange={e => updateExercise(i, 'title', e.target.value)} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Description (optional)</label>
                        <input placeholder="e.g. Seated knee extension" value={ex.description} onChange={e => updateExercise(i, 'description', e.target.value)} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div className="form-field">
                          <label className="form-label">Sets</label>
                          <input type="number" min="1" value={ex.sets} onChange={e => updateExercise(i, 'sets', e.target.value)} />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Reps</label>
                          <input type="number" min="1" value={ex.reps} onChange={e => updateExercise(i, 'reps', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Image (optional)</label>
                        <input type="file" accept="image/png,image/jpeg" onChange={e => updateExercise(i, 'file', e.target.files[0])} />
                        {ex.file && <p style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>✓ {ex.file.name}</p>}
                      </div>
                      <div className="form-field">
                        <label className="form-label">YouTube URL (optional)</label>
                        <input type="url" placeholder="https://youtube.com/watch?v=..." value={ex.videoUrl} onChange={e => updateExercise(i, 'videoUrl', e.target.value)} />
                      </div>
                      <div onClick={() => updateExercise(i, 'saveToLibrary', !ex.saveToLibrary)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: ex.saveToLibrary !== false ? 'var(--brand-light)' : 'var(--bg)', border: '1.5px solid ' + (ex.saveToLibrary !== false ? 'var(--brand)' : 'var(--border)'), borderRadius: 8, cursor: 'pointer' }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, background: ex.saveToLibrary !== false ? 'var(--brand)' : 'white', border: '1.5px solid ' + (ex.saveToLibrary !== false ? 'var(--brand)' : 'var(--border-strong)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {ex.saveToLibrary !== false && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: ex.saveToLibrary !== false ? 'var(--brand)' : 'var(--text-secondary)', fontWeight: ex.saveToLibrary !== false ? 600 : 400 }}>Save to exercise library for future use</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" className="secondary" onClick={addExercise} style={{ width: '100%', marginBottom: 12, padding: 12 }}>
                + Add another exercise
              </button>

              <button onClick={handleSaveExercises} disabled={saving} style={{ width: '100%', padding: 14, fontSize: 15 }}>
                {saving ? 'Saving...' : 'Save and continue →'}
              </button>
            </div>
          )}

          {/* Invite */}
          {step === 'invite' && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px' }}>Step 3 of 3</p>
                <h1 style={{ marginBottom: 8 }}>Invite your first patient</h1>
                <p>They will receive an email with a link to set up their account and see their program.</p>
              </div>
              <div className="card">
                {inviteSent ? (
                  <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                    <div style={{ fontSize: 48, marginBottom: '1rem' }}>📧</div>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16, marginBottom: 8 }}>Invite sent!</p>
                    <p style={{ fontSize: 14, marginBottom: '1.5rem' }}>{inviteName} will receive an email to set up their account.</p>
                    <button onClick={() => setStep('done')} style={{ width: '100%', padding: 14, fontSize: 15 }}>
                      Continue →
                    </button>
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="form-field">
                      <label className="form-label">Patient name</label>
                      <input placeholder="Jane Smith" value={inviteName} onChange={e => setInviteName(e.target.value)} autoFocus />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Patient email</label>
                      <input type="email" placeholder="jane@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
                    </div>
                    <button onClick={handleInvite} disabled={saving || !inviteName.trim() || !inviteEmail.trim()} style={{ padding: 14, fontSize: 15 }}>
                      {saving ? 'Sending...' : 'Send invite →'}
                    </button>
                    <button type="button" className="secondary" onClick={() => setStep('done')} style={{ padding: 12, fontSize: 14 }}>
                      Skip this step
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: '1.5rem' }}>🎉</div>
              <h1 style={{ marginBottom: 12 }}>You are all set!</h1>
              <p style={{ marginBottom: '2rem', fontSize: 16, lineHeight: 1.7 }}>
                Your clinic is ready. Head to your dashboard to assign programs to patients and track their compliance.
              </p>
              <button onClick={onComplete} style={{ width: '100%', padding: 16, fontSize: 16 }}>
                Go to dashboard →
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}